import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Same as React's `useState`, but also returns a `ref` that always points
 * to the latest state value.
 */
export function useStateWithRef<T>(
  initialState: T | (() => T)
): [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  React.MutableRefObject<T | undefined>
] {
  const init = useRef(false);

  const ref = useRef<T | undefined>(undefined);
  if (!init.current) {
    ref.current =
      typeof initialState === 'function'
        ? (initialState as () => T)()
        : initialState;
    init.current = true;
  }

  // cleanup function to avoid memory leaks
  useEffect(() => {
    return () => {
      ref.current = undefined;
    };
  }, []);

  const [state, setStateInner] = useState<T>(() => ref.current as T);

  const setState: React.Dispatch<React.SetStateAction<T>> = useCallback(
    (value) => {
      setStateInner((state) => {
        let next: T;
        if (typeof value === 'function') {
          next = (value as (prevState: T) => T)(state);
        } else {
          next = value;
        }
        ref.current = next;
        return next;
      });
    },
    []
  );

  return [state, setState, ref];
}
